import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import EventInviteShortlist from "../components/EventInviteShortlist";
import EventLabelEditor from "../components/EventLabelEditor";
import PageHeader from "../components/PageHeader";
import { MEMBER_EVENTS_REVIEW } from "../gql/queries/eventMatching";
import { ZONE_COLOURS } from "../helpers/const";
import { Spacing } from "../types/enum";
import { type ZoneLabel } from "../types/graphql";

interface Label {
  key: string;
  name: string;
  family: string;
}
interface ReviewRow {
  eventId: string;
  startDate: string | number;
  zone: ZoneLabel;
  isPublic: boolean;
  priority: string;
  title: string;
  kind: string;
  englishLevel: string;
  hostName: string;
  hostBadge: string;
  isFirstPublic: boolean;
  acceptedInvites: number;
  spotsTaken: number;
  maxParticipants: number;
  spotsLeft?: number | null;
  labelsEdited: boolean;
  labels: Label[];
}

const ZoneChip: React.FC<{ zone?: ZoneLabel | null }> = ({ zone }) =>
  zone ? (
    <Chip
      size="small"
      label={zone}
      sx={{ backgroundColor: ZONE_COLOURS.get(zone), color: "#fff", fontWeight: 600 }}
    />
  ) : null;

const kindLabel = (kind: string): string =>
  kind.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const PUBLIC_LABEL = "Public";
const PRIVATE_LABEL = "Private";
const VISIBILITY_OPTIONS = [PUBLIC_LABEL, PRIVATE_LABEL];

/**
 * One multi-select filter over the board's events. Nothing ticked means "Any",
 * so the board starts unfiltered and each filter only ever narrows it.
 */
const MultiFilter: React.FC<{
  label: string;
  value: string[];
  options: string[];
  onChange: (next: string[]) => void;
  /** Prettify an option for display (the stored value stays raw). */
  display?: (option: string) => string;
}> = ({ label, value, options, onChange, display }) => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(event) => {
      // MUI types a multiple-select's value as string; it is really string[].
      const raw = event.target.value as unknown as string[] | string;
      onChange(typeof raw === "string" ? raw.split(",") : raw);
    }}
    sx={{ minWidth: 150 }}
    InputLabelProps={{ shrink: true }}
    SelectProps={{
      multiple: true,
      displayEmpty: true,
      renderValue: (selected) => {
        const list = selected as string[];
        if (list.length === 0) {
          return (
            <Box component="span" sx={{ color: "text.secondary" }}>
              Any
            </Box>
          );
        }
        return list.map((option) => display?.(option) ?? option).join(", ");
      },
    }}
  >
    {options.map((option) => (
      <MenuItem key={option} value={option}>
        <Checkbox size="small" checked={value.includes(option)} />
        {display?.(option) ?? option}
      </MenuItem>
    ))}
  </TextField>
);

const MemberEventsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [openEvent, setOpenEvent] = useState<ReviewRow | null>(null);
  const [labelEvent, setLabelEvent] = useState<ReviewRow | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<Set<string>>(new Set());
  const [zoneFilter, setZoneFilter] = useState<Set<string>>(new Set());
  const [visibilityFilter, setVisibilityFilter] = useState<string[]>([]);
  const [hostFilter, setHostFilter] = useState<string[]>([]);
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [kindFilter, setKindFilter] = useState<string[]>([]);
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  // Bumped after a label edit, to pull an open shortlist again.
  const [shortlistRefresh, setShortlistRefresh] = useState(0);

  const { data, loading, refetch } = useQuery(MEMBER_EVENTS_REVIEW, {
    variables: { days: 30 },
    fetchPolicy: "cache-and-network",
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const rows: ReviewRow[] = data?.adminMemberEventsReview ?? [];
  const priorities = useMemo(() => [...new Set(rows.map((r) => r.priority))], [rows]);
  const zones = useMemo(() => [...new Set(rows.map((r) => r.zone))], [rows]);

  // Filter options come from the events actually on the board, so they never
  // offer a choice that would return nothing.
  const hosts = useMemo(
    () => [...new Set(rows.map((r) => r.hostName).filter(Boolean))].sort(),
    [rows],
  );
  const levels = useMemo(
    () => [...new Set(rows.map((r) => r.englishLevel).filter(Boolean))].sort(),
    [rows],
  );
  const kinds = useMemo(() => [...new Set(rows.map((r) => r.kind).filter(Boolean))].sort(), [rows]);
  const labelNames = useMemo(
    () => [...new Set(rows.flatMap((r) => r.labels.map((l) => l.name)))].sort(),
    [rows],
  );

  const visible = rows.filter(
    (r) =>
      (priorityFilter.size === 0 || priorityFilter.has(r.priority)) &&
      (zoneFilter.size === 0 || zoneFilter.has(r.zone)) &&
      (visibilityFilter.length === 0 ||
        visibilityFilter.includes(r.isPublic ? PUBLIC_LABEL : PRIVATE_LABEL)) &&
      (hostFilter.length === 0 || hostFilter.includes(r.hostName)) &&
      (levelFilter.length === 0 || levelFilter.includes(r.englishLevel)) &&
      (kindFilter.length === 0 || kindFilter.includes(r.kind)) &&
      // An event matches if it carries ANY of the ticked labels.
      (labelFilter.length === 0 || r.labels.some((l) => labelFilter.includes(l.name))),
  );

  const toggle = (set: Set<string>, value: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const hasFilters =
    priorityFilter.size > 0 ||
    zoneFilter.size > 0 ||
    visibilityFilter.length > 0 ||
    hostFilter.length > 0 ||
    levelFilter.length > 0 ||
    kindFilter.length > 0 ||
    labelFilter.length > 0;

  const clearFilters = () => {
    setPriorityFilter(new Set());
    setZoneFilter(new Set());
    setVisibilityFilter([]);
    setHostFilter([]);
    setLevelFilter([]);
    setKindFilter([]);
    setLabelFilter([]);
  };

  return (
    <>
      <PageHeader title="Member Events">
        Upcoming community events (hosted by members and Big Sisters), most in need of people first.
        Open <strong>Find members</strong> on any event to see the members most likely to want it —
        the shortlist opens below the table so you can keep scanning events.
      </PageHeader>

      <Card>
        <Box p={Spacing.l}>
          <Stack spacing={Spacing.s} mb={Spacing.m}>
            {priorities.length > 0 && (
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <Typography variant="caption" color="textSecondary" sx={{ minWidth: 70 }}>
                  Priority
                </Typography>
                {priorities.map((p) => (
                  <Chip
                    key={p}
                    size="small"
                    label={p}
                    variant={priorityFilter.has(p) ? "filled" : "outlined"}
                    color={priorityFilter.has(p) ? "primary" : "default"}
                    onClick={() => toggle(priorityFilter, p, setPriorityFilter)}
                  />
                ))}
              </Box>
            )}
            {zones.length > 0 && (
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <Typography variant="caption" color="textSecondary" sx={{ minWidth: 70 }}>
                  Zone
                </Typography>
                {zones.map((z) => (
                  <Chip
                    key={z}
                    size="small"
                    label={z}
                    variant={zoneFilter.has(z) ? "filled" : "outlined"}
                    color={zoneFilter.has(z) ? "primary" : "default"}
                    onClick={() => toggle(zoneFilter, z, setZoneFilter)}
                  />
                ))}
              </Box>
            )}
            {rows.length > 0 && (
              <Box display="flex" alignItems="center" gap={Spacing.s} flexWrap="wrap">
                <MultiFilter
                  label="Visibility"
                  value={visibilityFilter}
                  options={VISIBILITY_OPTIONS}
                  onChange={setVisibilityFilter}
                />
                <MultiFilter
                  label="Host"
                  value={hostFilter}
                  options={hosts}
                  onChange={setHostFilter}
                />
                <MultiFilter
                  label="Level"
                  value={levelFilter}
                  options={levels}
                  onChange={setLevelFilter}
                />
                <MultiFilter
                  label="Kind"
                  value={kindFilter}
                  options={kinds}
                  onChange={setKindFilter}
                  display={kindLabel}
                />
                <MultiFilter
                  label="Label"
                  value={labelFilter}
                  options={labelNames}
                  onChange={setLabelFilter}
                />
                {hasFilters && (
                  <Button size="small" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </Box>
            )}
          </Stack>

          {loading && rows.length === 0 ? (
            <Box display="flex" justifyContent="center" p={Spacing.xxl}>
              <CircularProgress />
            </Box>
          ) : visible.length === 0 ? (
            <Alert severity="info">
              {rows.length === 0
                ? "No upcoming community events in the next 30 days."
                : "No events match these filters."}
            </Alert>
          ) : (
            <>
              <Typography variant="caption" color="textSecondary" display="block" mb={Spacing.xs}>
                {visible.length} event{visible.length === 1 ? "" : "s"} — scroll within the table.
              </Typography>
              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Zone</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Kind</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Host</TableCell>
                      <TableCell align="right">RSVPs</TableCell>
                      <TableCell align="right">Spots</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.map((row) => (
                      <TableRow
                        key={row.eventId}
                        hover
                        selected={openEvent?.eventId === row.eventId}
                      >
                        <TableCell>
                          <Typography variant="caption">
                            {format(new Date(row.startDate), "EEE d MMM, HH:mm")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ZoneChip zone={row.zone} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{row.priority}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.title}</Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.25}>
                            {row.labels.length ? (
                              row.labels.map((l) => (
                                <Chip
                                  key={l.key}
                                  label={l.name}
                                  size="small"
                                  variant="outlined"
                                  color={l.family === "skill" ? "secondary" : "primary"}
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                none
                              </Typography>
                            )}
                            <Chip
                              label={row.labelsEdited ? "✏️ edited" : "✏️"}
                              size="small"
                              variant="outlined"
                              title="Edit the labels for this event's series"
                              onClick={() => setLabelEvent(row)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{kindLabel(row.kind)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{row.englishLevel}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{row.hostName}</Typography>
                          {row.isFirstPublic && (
                            <Typography variant="caption" display="block" color="secondary">
                              🎉 first
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{row.acceptedInvites}</TableCell>
                        <TableCell align="right">
                          {/* Seats held = every invite not declined, not just the RSVPs. */}
                          <Typography variant="caption">
                            {row.maxParticipants < 0
                              ? "∞"
                              : `${row.spotsTaken}/${row.maxParticipants}`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {row.isPublic ? (
                            <Button
                              size="small"
                              onClick={() =>
                                setOpenEvent(openEvent?.eventId === row.eventId ? null : row)
                              }
                            >
                              {openEvent?.eventId === row.eventId ? "Hide" : "🔎 Find members"}
                            </Button>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              Private
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Card>

      {labelEvent && (
        <EventLabelEditor
          title={labelEvent.title}
          current={labelEvent.labels.map((l) => l.key)}
          edited={labelEvent.labelsEdited}
          onClose={() => setLabelEvent(null)}
          onSaved={() => {
            setLabelEvent(null);
            refetch();
            // Saving and "Back to automatic" both land here. Labels decide the
            // shortlist, so an open one has to be pulled again too.
            setShortlistRefresh((n) => n + 1);
          }}
        />
      )}

      {/* The shortlist opens below the whole table. */}
      {openEvent && (
        <Box mt={Spacing.m}>
          <EventInviteShortlist
            eventId={openEvent.eventId}
            title={openEvent.title}
            zone={openEvent.zone}
            maxParticipants={openEvent.maxParticipants}
            onClose={() => setOpenEvent(null)}
            refreshToken={shortlistRefresh}
          />
        </Box>
      )}
    </>
  );
};

export default MemberEventsPage;
